/**
 * scheduleDraftCanvasHelpers — ScheduleDraft 多轨画布模型的纯工具层。
 *
 * 本模块负责航班身份匹配、差异状态、轨道文案、时间窗口与需求参考占位等
 * 画布投影细节。buildScheduleDraftCanvasModel 组合这些纯函数，组件层只消费
 * 已完成的 tracks/items ViewModel。
 *
 * 设计文档 → docs/20_workspace_ux/20_multitrack_view.md
 * 骨架关联 → lib/canvas/buildScheduleDraftCanvasModel.js (画布投影), lib/trackUtils.js (轨集合), hooks/useScheduleDraftEditorData.js (模型消费)
 */

import { createDiffCountMap, DIFF_STATUS, DIFF_STATUS_META } from "./scheduleDraftCanvasConstants";
import { formatTimelineClockTime } from "./timelineVisualSemantics";
import { getFlightTimeDisplayInfo } from "../flightTimingUtils";
import { formatTrackTypeLabel, getDraftFlights, getHistoryTracks, getTrackFlights, isExecutionReferenceSource, toArray } from "../trackUtils";

const DAY_MS = 24 * 60 * 60 * 1000;
const DELETED_CHANGE_STATUS = "DELETED";

export { getTrackFlights, toArray };

/**
 * 将任意值规范为字符串。
 * @param {*} value - 待规范值。
 * @param {string} fallback - 空值时使用的默认文本。
 * @returns {string} 字符串结果。
 */
export function normalizeString(value, fallback = "") {
    return value == null ? fallback : String(value);
}

function formatTimelineTime(value) {
    return normalizeString(value, "--").replace("T", " ");
}

function normalizeFlightTupleKey(flight) {
    return [
        normalizeString(flight?.flight_number).trim().toUpperCase(),
        normalizeString(flight?.departure_airport).trim().toUpperCase(),
        normalizeString(flight?.arrival_airport).trim().toUpperCase(),
        normalizeString(flight?.std),
        normalizeString(flight?.sta),
    ].join("|");
}

function normalizeFlightDateKey(flight) {
    const value = flight?.flight_date || flight?.std;
    if (!value) {
        return "";
    }

    if (value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value.toISOString().slice(0, 10) : "";
    }

    const text = normalizeString(value).trim();
    const dateMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
        return dateMatch[1];
    }

    const parsed = new Date(text);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString().slice(0, 10) : text;
}

function normalizeBusinessKeyParts(flight) {
    return {
        aircraftReg: normalizeString(flight?.aircraft_reg).trim().toUpperCase(),
        flightNumber: normalizeString(flight?.flight_number).trim().toUpperCase(),
        flightDate: normalizeFlightDateKey(flight),
        departureAirport: normalizeString(flight?.departure_airport).trim().toUpperCase(),
        arrivalAirport: normalizeString(flight?.arrival_airport).trim().toUpperCase(),
    };
}

/**
 * 返回航班用于主匹配的首个身份 key。
 * @param {Object|null} flight - 航班对象。
 * @returns {string} 航班身份 key。
 */
export function getFlightIdentityKey(flight) {
    return getFlightIdentityKeys(flight)[0];
}

/**
 * 构建航班跨来源匹配使用的身份 key 列表。
 * @param {Object|null} flight - 航班对象。
 * @returns {string[]} 从强 ID 到弱 tuple 的身份 key 列表。
 */
export function getFlightIdentityKeys(flight) {
    const keys = [];

    if (flight?.id) {
        keys.push(`id:${flight.id}`);
    }

    if (flight?.origin_track_id && flight?.id) {
        keys.push(`origin-track:${flight.origin_track_id}:${flight.id}`);
    }

    if (flight?.origin_v1_flight_id) {
        keys.push(`origin-v1:${flight.origin_v1_flight_id}`);
    }

    if (flight?.source_flight_id && flight?.source_type === "EXECUTION") {
        keys.push(`operation:${flight.source_flight_id}`);
    }

    if (flight?.source_operation_id) {
        keys.push(`operation:${flight.source_operation_id}`);
    }

    const { aircraftReg, flightNumber, flightDate, departureAirport, arrivalAirport } = normalizeBusinessKeyParts(flight);
    if (flightNumber && flightDate && departureAirport && arrivalAirport) {
        keys.push(`business:${flightNumber}:${flightDate}:${departureAirport}:${arrivalAirport}`);
        if (aircraftReg) {
            keys.push(`business-aircraft:${aircraftReg}:${flightNumber}:${flightDate}:${departureAirport}:${arrivalAirport}`);
        }
    }

    keys.push(`tuple:${normalizeFlightTupleKey(flight)}`);
    return [...new Set(keys)];
}

/**
 * 构建按航班身份 key 索引的查找表。
 * @param {Array<Object>} flights - 航班集合。
 * @returns {Map<string, Object>} 航班查找表。
 */
export function buildFlightLookup(flights) {
    const lookup = new Map();
    toArray(flights).forEach((flight) => {
        getFlightIdentityKeys(flight).forEach((key) => {
            if (!lookup.has(key)) {
                lookup.set(key, flight);
            }
        });
    });
    return lookup;
}

/**
 * 比较两个航班的核心展示字段是否一致。
 * @param {Object|null} left - 左侧航班。
 * @param {Object|null} right - 右侧航班。
 * @returns {boolean} 核心字段一致时返回 true。
 */
export function areFlightCoreFieldsEqual(left, right) {
    if (!left || !right) {
        return false;
    }

    const keys = [
        "aircraft_reg",
        "flight_number",
        "departure_airport",
        "arrival_airport",
        "std",
        "sta",
        "is_fixed",
        "aircraft_type",
        "stc_type",
        "block_time_minutes",
        "flight_route_id",
        "rotation_no",
    ];
    return keys.every((key) => normalizeString(left[key]) === normalizeString(right[key]));
}

/**
 * 按 aircraft_reg 分组航班。
 * @param {Array<Object>} flights - 航班集合。
 * @returns {Record<string, Object[]>} 以飞机注册号为 key 的航班组。
 */
export function groupFlightsByAircraft(flights) {
    return toArray(flights).reduce((accumulator, flight) => {
        const aircraftKey = normalizeString(flight?.aircraft_reg, "UNASSIGNED") || "UNASSIGNED";
        if (!accumulator[aircraftKey]) {
            accumulator[aircraftKey] = [];
        }
        accumulator[aircraftKey].push(flight);
        return accumulator;
    }, {});
}

/**
 * 从航班或草稿周期推导画布时间窗口。
 * @param {Array<Object>} flights - 当前参与投影的航班集合。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @returns {{start: string, end: string}} 画布时间窗口。
 */
export function getTimeWindowFromFlights(flights, draft) {
    const validTimes = toArray(flights)
        .flatMap((flight) => [flight?.std, flight?.sta])
        .filter(Boolean)
        .map((value) => new Date(value).getTime())
        .filter((value) => Number.isFinite(value));

    if (validTimes.length) {
        const min = Math.min(...validTimes) - DAY_MS / 2;
        const max = Math.max(...validTimes) + DAY_MS / 2;
        return {
            start: new Date(min).toISOString(),
            end: new Date(max).toISOString(),
        };
    }

    if (draft?.period_start || draft?.period_end) {
        const start = draft?.period_start ? new Date(`${draft.period_start}T00:00:00Z`).getTime() : Date.now() - DAY_MS;
        const end = draft?.period_end ? new Date(`${draft.period_end}T23:59:59Z`).getTime() : Date.now() + DAY_MS;
        return {
            start: new Date(start).toISOString(),
            end: new Date(end).toISOString(),
        };
    }

    return {
        start: new Date(Date.now() - DAY_MS).toISOString(),
        end: new Date(Date.now() + DAY_MS).toISOString(),
    };
}

export function buildItemTimeTooltipLines(flight, { useEffectiveTime = false } = {}) {
    if (!useEffectiveTime) {
        return [];
    }

    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime: true });
    const lines = [
        `${timeInfo.departureLabel} (${timeInfo.departureSourceLabel}): ${formatTimelineTime(timeInfo.departureTime)}`,
        `${timeInfo.arrivalLabel} (${timeInfo.arrivalSourceLabel}): ${formatTimelineTime(timeInfo.arrivalTime)}`,
    ];

    if (timeInfo.departureSourceLabel === "ETD" && flight?.std) {
        lines.push(`原 STD: ${formatTimelineTime(flight.std)}`);
    }
    if (timeInfo.arrivalSourceLabel === "ETA" && flight?.sta) {
        lines.push(`原 STA: ${formatTimelineTime(flight.sta)}`);
    }

    lines.push("ATD/ATA 不参与编排定位");
    return lines;
}

/**
 * 获取当前主参考轨。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @param {Array<string|number>} visibleReferenceTrackIds - 已展开轨 ID。
 * @returns {Object|null} 主参考轨。
 */
export function getPrimaryReferenceTrack(draft, visibleReferenceTrackIds) {
    const tracks = getHistoryTracks(draft);
    if (!tracks.length) {
        return null;
    }

    const tracksById = new Map(tracks.map((track) => [track.id, track]));
    const primaryId = toArray(visibleReferenceTrackIds)[0];
    return primaryId ? tracksById.get(primaryId) || null : null;
}

/**
 * 将差异计数转换为轨道状态徽标。
 * @param {Record<string, number>} counts - 差异状态计数。
 * @returns {Array<{text: string, tone: string}>} 徽标列表。
 */
export function buildStatusBadges(counts) {
    return Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([status, count]) => ({
            text: `${DIFF_STATUS_META[status].label} ${count}`,
            tone: DIFF_STATUS_META[status].tone,
        }));
}

/**
 * 计算航班相对基线的差异状态。
 * @param {Object} flight - 当前航班。
 * @param {Map<string, Object>} baselineLookup - 基线航班查找表。
 * @param {"editable"|"reference"} mode - 当前轨道模式。
 * @returns {string} DIFF_STATUS 值。
 */
export function getFlightDiffStatus(flight, baselineLookup, mode) {
    if (flight?.is_deleted || String(flight?.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS) {
        return DIFF_STATUS.DELETED;
    }

    const matchedFlight = getFlightIdentityKeys(flight).map((key) => baselineLookup.get(key)).find(Boolean);

    if (!matchedFlight) {
        return mode === "editable" ? DIFF_STATUS.ADDED : DIFF_STATUS.DELETED;
    }

    return areFlightCoreFieldsEqual(flight, matchedFlight) ? DIFF_STATUS.UNCHANGED : DIFF_STATUS.MODIFIED;
}

/**
 * 构建轨道上下文说明。
 * @param {string} trackKind - editable 或 reference。
 * @param {Object=} track - 参考轨对象。
 * @returns {string} 轨道上下文文案。
 */
export function buildTrackContext(trackKind, track) {
    if (trackKind === "editable") {
        return "当前航班计划中的可编辑航班编排轨。";
    }

    if (track) {
        const effectiveTimeNote = isExecutionReferenceSource(track) ? "；时间定位使用 ETD/ETA 优先，ATD/ATA 不参与编排" : "";
        return `${formatTrackTypeLabel(track.track_type, "参考轨")}${track.summary ? `：${track.summary}` : ""}${effectiveTimeNote}`;
    }

    return "参考轨";
}

/**
 * 构建轨道副标题。
 * @param {string} aircraftReg - 飞机注册号。
 * @param {Object=} track - 参考轨对象。
 * @returns {string} 副标题文案。
 */
export function buildTrackSubtitle(aircraftReg, track) {
    const regLabel = aircraftReg === "UNASSIGNED" ? "未分配飞机" : aircraftReg;
    return track ? `${regLabel} · ${track.name || formatTrackTypeLabel(track.track_type, "参考轨")}` : regLabel;
}

/**
 * 构建轨道标题。
 * @param {string} aircraftReg - 飞机注册号。
 * @param {string} trackKind - editable 或 reference。
 * @returns {string} 轨道标题。
 */
export function buildTrackLabel(aircraftReg, trackKind) {
    const regLabel = aircraftReg === "UNASSIGNED" ? "未分配飞机" : aircraftReg;
    return trackKind === "editable" ? regLabel : `${regLabel} 参考轨`;
}

/**
 * 构建航班 item 的起降时间标签。
 * @param {Object} flight - 航班对象。
 * @returns {{departure: string, arrival: string}} 起飞和落地显示时间。
 */
export function buildItemTimeLabels(flight, { useEffectiveTime = false } = {}) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    return {
        departure: formatTimelineClockTime(timeInfo.departureTime),
        arrival: formatTimelineClockTime(timeInfo.arrivalTime),
    };
}

export function buildItemSubtitle(flight, { useEffectiveTime = false } = {}) {
    return buildItemTimeLabels(flight, { useEffectiveTime }).departure;
}

/**
 * 构建需求参考区的占位或派生模型。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @param {boolean} showDemandReferences - 是否显示需求参考。
 * @returns {Array<Object>} 需求参考模型。
 */
export function buildDemandReferences(draft, showDemandReferences) {
    if (!showDemandReferences) {
        return [];
    }

    const flights = getDraftFlights(draft);
    if (!flights.length) {
        return [
            {
                id: `${draft?.id || "draft"}-demand-placeholder`,
                title: draft?.name || "当前航班计划需求参考位",
                roleLabel: "需求参考",
            },
        ];
    }

    return [
        {
            id: `${draft?.id || "draft"}-demand-derived`,
            title: `航班计划需求参考 · ${draft?.name || "航班计划"}`,
            roleLabel: "需求参考",
        },
    ];
}

/**
 * 推导初始展开的历史快照轨 ID 列表。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @returns {Array<string|number>} 默认展开的轨 ID。
 */
export function getInitialExpandedTrackIds(draft) {
    const tracks = getHistoryTracks(draft);
    return tracks.length ? [tracks[0].id] : [];
}

/**
 * 汇总航班集合相对基线的差异状态计数。
 * @param {Array<Object>} flights - 航班集合。
 * @param {Map<string, Object>} baselineLookup - 基线航班查找表。
 * @param {"editable"|"reference"} mode - 当前轨道模式。
 * @returns {Record<string, number>} 差异状态计数。
 */
export function countDiffStatuses(flights, baselineLookup, mode) {
    return flights
        .map((flight) => getFlightDiffStatus(flight, baselineLookup, mode))
        .reduce((accumulator, status) => ({ ...accumulator, [status]: accumulator[status] + 1 }), createDiffCountMap());
}
